import type { FC, ReactNode } from 'react'
import type { ConsortiumCategory } from '@/types'

import Link from 'next/link'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { getLongestTerm, getStartingInstallment, simulateConsortium } from '@/lib/consortium'
import { formatCompactCurrency, formatCurrency } from '@/lib/format'
import { buttonClassName } from '@/components/generals/button'
import { LogoMark } from '@/components/generals/logo'
import { ConsortiumIcon } from '@/components/landing/consortium-icon'

interface CategoryHeroProps {
    category: ConsortiumCategory
}

export const CategoryHero: FC<CategoryHeroProps> = ({ category }): ReactNode => {
    const example = simulateConsortium(category, category.credit.initial, category.initialTerm)
    const shortestTerm = Math.min(...category.terms)

    const facts = [
        {
            label: 'Créditos',
            value: `${formatCompactCurrency(category.credit.min)} a ${formatCompactCurrency(category.credit.max)}`
        },
        {
            label: 'Prazos',
            value: `${shortestTerm} a ${getLongestTerm(category)} meses`
        },
        {
            label: 'Parcelas desde',
            value: `${formatCurrency(getStartingInstallment(category))}/mês`
        }
    ]

    return (
        <section aria-labelledby='categoria-titulo' className='relative overflow-hidden'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-dot-grid [mask-image:radial-gradient(ellipse_at_top_left,black_5%,transparent_60%)]'
            />

            <div className='relative mx-auto max-w-7xl px-5 pb-20 pt-6 sm:px-8 sm:pt-10 lg:pb-28'>
                <nav aria-label='Você está em'>
                    <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                        <li>
                            <Link href='/' className='rounded transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'>
                                Início
                            </Link>
                        </li>

                        <li aria-hidden='true'>
                            <ChevronRight className='size-4' />
                        </li>

                        <li>
                            <Link href='/#consorcios' className='rounded transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'>
                                Consórcios
                            </Link>
                        </li>

                        <li aria-hidden='true'>
                            <ChevronRight className='size-4' />
                        </li>

                        <li aria-current='page' className='font-semibold text-foreground'>
                            {category.name}
                        </li>
                    </ol>
                </nav>

                <div className='mt-10 grid items-center gap-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14'>
                    <div className='motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-700'>
                        <p className='inline-flex items-center gap-2.5 rounded-full border border-border bg-card/80 px-4 py-1.5 text-sm font-semibold text-foreground/80 shadow-xs'>
                            <ConsortiumIcon slug={category.slug} className='size-4 text-bronze' />
                            {category.title}
                        </p>

                        <h1
                            id='categoria-titulo'
                            className='mt-7 text-balance font-heading text-[3rem] leading-[0.98] tracking-tight text-foreground sm:text-7xl lg:text-[5rem]'
                        >
                            {category.headline} <em className='text-bronze'>{category.headlineAccent}</em>
                        </h1>

                        <p className='mt-7 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                            {category.description}
                        </p>

                        <div className='mt-10 flex flex-col gap-3 sm:flex-row'>
                            <Link href='#simulador' className={buttonClassName({ size: 'lg' })}>
                                Simular minha parcela
                                <ArrowRight aria-hidden='true' className='size-5' />
                            </Link>

                            <Link href='#contato' className={buttonClassName({ variant: 'outline', size: 'lg' })}>
                                Receber uma proposta
                            </Link>
                        </div>
                    </div>

                    <div className='relative mx-auto w-full max-w-md motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-700 lg:max-w-none'>
                        <div aria-hidden='true' className='absolute inset-x-8 inset-y-6 -rotate-6 rounded-[2rem] bg-gold/70' />

                        <div className='relative isolate overflow-hidden rounded-[2rem] bg-ink p-8 text-primary-foreground shadow-2xl shadow-ink/30 sm:p-10'>
                            <div
                                aria-hidden='true'
                                className='pointer-events-none absolute inset-0 -z-10 bg-dot-grid-light [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]'
                            />

                            <ConsortiumIcon
                                slug={category.slug}
                                className='pointer-events-none absolute -right-12 -top-12 -z-10 size-60 text-gold/10'
                            />

                            <div className='flex items-center justify-between gap-4'>
                                <LogoMark className='size-11 rounded-[11px] ring-1 ring-gold/30' />

                                <span className='rounded-full border border-gold/40 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-gold'>
                                    Carta de crédito
                                </span>
                            </div>

                            <p className='mt-14 text-sm text-primary-foreground/70'>
                                Exemplo de crédito
                            </p>

                            <p className='mt-1 font-heading text-5xl tabular-nums sm:text-6xl'>
                                {formatCurrency(example.credit)}
                            </p>

                            <dl className='mt-10 grid grid-cols-2 gap-6 border-t border-ink-line pt-6'>
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-primary-foreground/55'>
                                        Parcela estimada
                                    </dt>

                                    <dd className='mt-1 text-xl font-bold tabular-nums'>
                                        {formatCurrency(example.installment, { cents: true })}
                                    </dd>
                                </div>

                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-primary-foreground/55'>
                                        Prazo
                                    </dt>

                                    <dd className='mt-1 text-xl font-bold'>
                                        {example.term} meses
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                <dl className='mt-16 grid gap-4 sm:grid-cols-3 lg:mt-20'>
                    {facts.map(fact => (
                        <div key={fact.label} className='rounded-[1.5rem] bg-card px-6 py-5 ring-1 ring-border'>
                            <dt className='text-xs font-bold uppercase tracking-[0.16em] text-bronze'>
                                {fact.label}
                            </dt>

                            <dd className='mt-2 font-heading text-3xl tabular-nums text-foreground'>
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
